import { api } from '@mmef/shared/api';
import type { CurrentUser, MessageResult } from '@mmef/shared/api';

/**
 * Passwordless auth. `requestMagicLink` and `verify` sit on the backend's public filter chain
 * (no CSRF token needed yet); `getMe` runs through the session chain, which is also what mints
 * the XSRF-TOKEN cookie the rest of the app needs before it can write anything.
 */
export const authApi = api.injectEndpoints({
  endpoints: (build) => ({
    requestMagicLink: build.mutation<MessageResult, string>({
      query: (email) => ({
        url: '/auth/magic-link',
        method: 'POST',
        body: { email },
      }),
    }),

    verifyMagicLink: build.mutation<CurrentUser, string>({
      query: (token) => ({
        url: '/auth/verify',
        method: 'POST',
        body: { token },
      }),
      // A fresh session invalidates everything account-scoped.
      invalidatesTags: ['Me', 'Subscriptions', 'AdminEvents'],
    }),

    getMe: build.query<CurrentUser, void>({
      query: () => '/me',
      providesTags: ['Me'],
    }),

    updateProfile: build.mutation<CurrentUser, { displayName: string | null }>({
      query: (body) => ({ url: '/me/profile', method: 'PUT', body }),
      // The nav shows the name, so it has to re-read rather than wait for a reload.
      invalidatesTags: ['Me'],
    }),

    signOut: build.mutation<void, void>({
      query: () => ({ url: '/me/session', method: 'DELETE' }),
      invalidatesTags: ['Me', 'Subscriptions', 'AdminEvents'],
    }),
  }),
});

export const {
  useRequestMagicLinkMutation,
  useVerifyMagicLinkMutation,
  useGetMeQuery,
  useUpdateProfileMutation,
  useSignOutMutation,
} = authApi;
